import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export interface OtherLink {
  label: string;
  url: string;
}

export interface GuidePhoto {
  id: number;
  publicUrl: string;
  caption: string | null;
  displayOrder: number;
}

export interface CheckinGuideConfig {
  id: number | null;
  enabled: boolean;
  showAddress: boolean;
  showKeyAccess: boolean;
  showWifi: boolean;
  showParking: boolean;
  showContact: boolean;
  showDetailedGuide: boolean;
  showPhotos: boolean;
  showOtherLinks: boolean;
  showAttentionNotes: boolean;
  showCheckoutInstructions: boolean;
  showWeather: boolean;
  checkInTime: string | null;
  checkOutTime: string | null;
  address: string | null;
  addressRemark: string | null;
  addressRemarkEn: string | null;
  keyAccessType: 'DOOR_CODE' | 'KEYBOX' | 'PERSON' | 'NONE' | 'TTLOCK' | null;
  keyCode: string | null;
  /** "HH:mm" — null means always visible */
  keyCodeVisibleFrom: string | null;
  keyRemark: string | null;
  keyRemarkEn: string | null;
  wifiName: string | null;
  wifiPassword: string | null;
  parkingInfo: string | null;
  parkingInfoEn: string | null;
  parkingPrice: string | null;
  hostContactName: string | null;
  hostContactPhone: string | null;
  hostContactEmail: string | null;
  detailedGuide: string | null;
  detailedGuideEn: string | null;
  attentionNotes: string | null;
  attentionNotesEn: string | null;
  checkoutInstructions: string | null;
  checkoutInstructionsEn: string | null;
  otherLinks: OtherLink[];
  photos: GuidePhoto[];
}

export interface PublicCheckinGuide {
  apartmentName: string;
  status: 'NOT_READY' | 'READY' | 'EXPIRED';
  checkInDate: string;
  checkOutDate: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  showAddress: boolean;
  showKeyAccess: boolean;
  showWifi: boolean;
  showParking: boolean;
  showContact: boolean;
  showDetailedGuide: boolean;
  showPhotos: boolean;
  showOtherLinks: boolean;
  showAttentionNotes: boolean;
  showCheckoutInstructions: boolean;
  address: string | null;
  addressRemark: string | null;
  keyAccessType: 'DOOR_CODE' | 'KEYBOX' | 'PERSON' | 'NONE' | 'TTLOCK' | null;
  /** null when keyCodeHidden=true — code is never sent before the reveal time */
  keyCode: string | null;
  keyCodeHidden: boolean;
  /** "HH:mm" on check-in day when code will be revealed; present only when keyCodeHidden=true */
  keyCodeVisibleFromTime: string | null;
  keyRemark: string | null;
  wifiName: string | null;
  wifiPassword: string | null;
  parkingInfo: string | null;
  parkingPrice: string | null;
  hostContactName: string | null;
  hostContactPhone: string | null;
  hostContactEmail: string | null;
  detailedGuide: string | null;
  attentionNotes: string | null;
  checkoutInstructions: string | null;
  otherLinks: OtherLink[];
  photos: GuidePhoto[];
  /** TTLock smart lock PIN — null when no smart lock configured, before check-in time, or PIN is time-gated */
  ttlockPinCode: string | null;
  /** true when PIN exists but the keyCodeVisibleFrom reveal time hasn't arrived yet */
  ttlockPinHidden: boolean;
  /** "HH:mm" on check-in day when PIN will be revealed; present only when ttlockPinHidden=true */
  ttlockPinVisibleFromTime: string | null;
  /** true when last PIN generation failed because the lock had no gateway connection */
  /** No code to show and the last attempt to issue one failed — render the "contact your host" note. */
  ttlockCodeUnavailable?: boolean;
  /**
   * true when the entry code is being withheld because the deposit is not actually reserved.
   *
   * Deliberately separate from the `*Hidden` flags: those mean "not yet" and are meant to be
   * waited out, this means "act now". Rendering them the same way leaves a guest waiting for a
   * code that will never arrive on its own.
   */
  depositBlocksKey?: boolean;
  showWeather: boolean;
  /** Empty when unavailable (disabled, ungeocodable address, or API failure) — never more than 5 entries */
  weatherDays: WeatherDay[];
  /** true when language=en was requested but at least one content field had no English version, so the original was shown instead */
  contentPartiallyUntranslated: boolean;
  /** true when the manager force-opened the guide while city tax is still unpaid on a completed registration — the page shows a pay banner (#154) */
  cityTaxDue?: boolean;
}

export interface WeatherDay {
  /** "Check-in"/"Check-out" for the first/last day in the window, else a weekday abbreviation (e.g. "Sat") */
  label: string;
  date: string;
  weatherCode: number;
  tempHigh: number;
  tempLow: number;
}

export const emptyGuideConfig = (): CheckinGuideConfig => ({
  id: null,
  enabled: false,
  showAddress: true,
  showKeyAccess: true,
  showWifi: true,
  showParking: true,
  showContact: true,
  showDetailedGuide: true,
  showPhotos: true,
  showOtherLinks: true,
  showAttentionNotes: true,
  showCheckoutInstructions: true,
  showWeather: true,
  checkInTime: '15:00',
  checkOutTime: '11:00',
  address: '',
  addressRemark: '',
  addressRemarkEn: '',
  keyAccessType: null,
  keyCode: '',
  keyCodeVisibleFrom: '12:00',
  keyRemark: '',
  keyRemarkEn: '',
  wifiName: '',
  wifiPassword: '',
  parkingInfo: '',
  parkingInfoEn: '',
  parkingPrice: '',
  hostContactName: '',
  hostContactPhone: '',
  hostContactEmail: '',
  detailedGuide: '',
  detailedGuideEn: '',
  attentionNotes: '',
  attentionNotesEn: '',
  checkoutInstructions: '',
  checkoutInstructionsEn: '',
  otherLinks: [],
  photos: [],
});

// ── Manager endpoints ─────────────────────────────────────────────────────────

export const getCheckinGuideConfig = async (apartmentId: number, token: string): Promise<CheckinGuideConfig> => {
  const res = await axios.get(`${API_BASE}/apartments/${apartmentId}/checkin-guide`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const saveCheckinGuideConfig = async (apartmentId: number, config: CheckinGuideConfig, token: string): Promise<CheckinGuideConfig> => {
  const res = await axios.put(`${API_BASE}/apartments/${apartmentId}/checkin-guide`, config, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// ── Photo management endpoints ────────────────────────────────────────────────

export const uploadGuidePhoto = async (
  apartmentId: number,
  file: File,
  caption: string,
  token: string,
  onProgress?: (pct: number) => void,
): Promise<GuidePhoto> => {
  const form = new FormData();
  form.append('file', file);
  if (caption.trim()) form.append('caption', caption.trim());
  const res = await axios.post(`${API_BASE}/apartments/${apartmentId}/checkin-guide/photos`, form, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data;
};

export const deleteGuidePhoto = async (
  apartmentId: number,
  photoId: number,
  token: string,
): Promise<void> => {
  await axios.delete(`${API_BASE}/apartments/${apartmentId}/checkin-guide/photos/${photoId}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const reorderGuidePhotos = async (
  apartmentId: number,
  orderedIds: number[],
  token: string,
): Promise<void> => {
  await axios.put(`${API_BASE}/apartments/${apartmentId}/checkin-guide/photos/order`, orderedIds, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

// ── Public endpoint ───────────────────────────────────────────────────────────

export const getPublicCheckinGuide = async (formToken: string, language: 'cs' | 'en' = 'cs'): Promise<PublicCheckinGuide> => {
  const jwt = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  const res = await axios.get(`${API_BASE}/public/checkin/form/${formToken}/guide`, { headers, params: { language } });
  return res.data;
};

/** Shown on the "pay before guide" screen when the guide endpoint returns 402. Self-sufficient —
 * carries apartment name/dates/nights too, since the main guide payload is never received in this case. */
export interface PublicCityTaxSummary {
  paid: boolean;
  apartmentName: string;
  checkInDate: string;
  checkOutDate: string;
  nights: number;
  amount: number;
  currency: string;
  adultCount: number;
  childCount: number;
  disabledCount: number;
  adultRate: number;
  childRate: number;
  disabledRate: number;
  adultSubtotal: number;
  childSubtotal: number;
  disabledSubtotal: number;
  /** Cumulative amount actually collected so far — may be less than `amount` after a guest
   * correction raised the true total. */
  amountPaid: number;
  /** max(0, amount - amountPaid). Zero once fully settled, even if amountPaid overshoots it. */
  missingAmount: number;
}

export const getPublicCityTaxSummary = async (formToken: string): Promise<PublicCityTaxSummary> => {
  const res = await axios.get(`${API_BASE}/public/checkin/form/${formToken}/city-tax`);
  return res.data;
};

export const createCityTaxCheckoutSession = async (formToken: string): Promise<{ url: string }> => {
  const res = await axios.post(`${API_BASE}/public/checkin/form/${formToken}/city-tax/checkout-session`, {});
  return res.data;
};

// ── Manager overlay endpoints (require JWT + ownership) ───────────────────────

export interface ManagerGuideOverlay {
  reservationId: number;
  cleaningStatus: 'PENDING' | 'READY';
  /** Always returned regardless of check-in time restriction */
  ttlockPinCode: string | null;
  /** "OFFLINE" when last PIN generation failed because the lock had no gateway connection; null otherwise */
  ttlockPinError?: string | null;
  /** Always returned even when keyCodeHidden=true for guests */
  keyCode: string | null;
  ttlockEnabled: boolean;
  /** Apartment-level default reveal time ("HH:mm"), null = always visible */
  defaultVisibleFrom: string | null;
  /** Per-reservation override ("HH:mm"), null = no override */
  visibleFromOverride: string | null;
  /** Resolved reveal time: override if set, else default; null = always visible */
  effectiveVisibleFrom: string | null;
  /** Apartment guide config key code (null for TTLock apartments) */
  defaultKeyCode: string | null;
  /** Per-reservation key code override; null = no override */
  keyCodeOverride: string | null;
  /** Resolved key code: override if set, else defaultKeyCode */
  effectiveKeyCode: string | null;
  /** True when ttlockPinCode came from the cloud-only offline-passcode fallback (no push to device needed) */
  ttlockPinIsOffline: boolean;
}

export const setGuideAccessMode = async (formToken: string, mode: 'AUTO' | 'OPEN' | 'BLOCKED', token: string): Promise<void> => {
  await axios.put(`${API_BASE}/manager/guide/${formToken}/guide-access`, { mode }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

/** Manager-only escape hatch: manually override city tax. PAID with no amount settles fully to the
 * live-calculated total; PAID with an amount presets exactly that as paid (e.g. collected in cash
 * before the guest even started the form) — the auto-calculation still requests any difference.
 * EXEMPT waives tax entirely for this reservation, skipping the tax step for the guest. UNPAID
 * resets to untouched. */
export const setCityTaxPaymentStatus = async (
  formToken: string, status: 'PAID' | 'UNPAID' | 'EXEMPT', token: string, amount?: number
): Promise<void> => {
  await axios.put(`${API_BASE}/manager/guide/${formToken}/city-tax`, { status, amount }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const getManagerGuideOverlay = async (formToken: string, token: string): Promise<ManagerGuideOverlay> => {
  const res = await axios.get(`${API_BASE}/manager/guide/${formToken}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const regenerateTtlockPin = async (
  formToken: string,
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  token: string,
): Promise<string> => {
  const res = await axios.post(
    `${API_BASE}/manager/guide/${formToken}/ttlock/regenerate`,
    { startDate, startTime, endDate, endTime },
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
  return res.data.pinCode;
};

export const getOfflineTtlockPin = async (
  formToken: string,
  token: string,
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string,
): Promise<string> => {
  const res = await axios.post(
    `${API_BASE}/manager/guide/${formToken}/ttlock/offline-pin`,
    { startDate, startTime, endDate, endTime },
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
  return res.data.pinCode;
};

export const updateKeyCodeOverride = async (
  formToken: string,
  keyCode: string | null,
  token: string,
): Promise<void> => {
  await axios.put(
    `${API_BASE}/manager/guide/${formToken}/key-code-override`,
    { keyCode },
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
};

export const setTtlockPinManually = async (
  formToken: string,
  pinCode: string | null,
  token: string,
): Promise<void> => {
  await axios.put(
    `${API_BASE}/manager/guide/${formToken}/ttlock/pin-manual`,
    { pinCode },
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
};

export const updateVisibleFrom = async (
  formToken: string,
  visibleFrom: string | null,
  token: string,
): Promise<void> => {
  await axios.put(
    `${API_BASE}/manager/guide/${formToken}/visible-from`,
    { visibleFrom },
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
};

export const updateCleaningStatus = async (
  formToken: string,
  cleaningStatus: 'PENDING' | 'READY',
  token: string,
): Promise<void> => {
  await axios.put(
    `${API_BASE}/manager/guide/${formToken}/cleaning-status`,
    { cleaningStatus },
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
};
